import React, {memo, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import classNames from 'classnames';
import {Delete as DeleteIcon} from '@material-ui/icons';
import {CircularProgress, Button} from '@material-ui/core';
import {convertImageFormat, formatBytes, handleModalState, noop} from '@pluto/utils';
import FileImport from '@pluto/file-import-modal';
import ContextMenu from '@pluto/context-menu';
import {
    handleModalOpen,
    handleReplace,
    onActiveImgChange,
} from '../QCToolsArtworkHandlers';
import {
    setQcToolsArtworkToWait as setArtworksWait,
    retryQcToolsArtworkAction as retryArtwork,
    deleteQcToolsArtworkAction as deleteArtwork,
} from '../Redux/QCToolsArtworkActions';
import {
    Thumbnail,
    ThumbnailRefs,
    ArtworkType,
    ArtworkStatus,
} from '../QCToolsArtworkTypes&Const';
import {
    handleRetryArtwork,
    handleDeleteArtwork,
    getDeleteConfirmationDialog,
} from './QCToolsArtworkThumbnailsHandlers';

interface Props {
    currentId?: number;
    setCurrentId?: Function;
    currentZoomId: number;
    setCurrentZoomId: Function;
    thumbnail: Thumbnail;
    thumbnailRefs: ThumbnailRefs;
    handleZoomIn: Function;
    onDebouncedImageSelect?: Function;
    name: string;
    resX: number;
    resY: number;
    artworkType: ArtworkType;
    aspectRatio: string;
    imageFormat: string;
    fileSizeBytes: number;
    url: string;
    status?: string;
    id: number;
    qcId: string;
    isModalOpen: string;
    setArtworkReplace: () => void;
}

export const getSelectOpts = (defaultValue: string) => [
    {
        placeholder: 'Artwork type',
        enumType: 'artworkTypes',
        disabled: true,
        defaultValue,
    },
];

export const QCToolsArtworkThumbnailsItem = ({
    currentId,
    setCurrentId = noop,
    currentZoomId,
    setCurrentZoomId = noop,
    thumbnail,
    thumbnailRefs,
    handleZoomIn,
    onDebouncedImageSelect = noop,
    name,
    resX,
    resY,
    aspectRatio,
    imageFormat,
    fileSizeBytes,
    url,
    status,
    id,
    qcId,
    isModalOpen,
    artworkType,
    setArtworkReplace,
}: Props) => {
    const dispatch = useDispatch();

    const [modalVisible, setModalVisible] = useState(false);

    const handleParams = useMemo(
        () => ({
            id,
            qcId,
            setArtworksWait,
            deleteArtwork,
            retryArtwork,
            dispatch,
            setModalVisible,
        }),
        [id, qcId]
    );

    const handleRetryClick = handleRetryArtwork(handleParams);
    const handleDeleteClick = handleDeleteArtwork(handleParams);

    const handleZoom = useMemo(() => handleModalOpen(handleZoomIn, name), []);

    if (status === ArtworkStatus.failed) {
        return (
            <div className="thumbnails-item thumbnails-item-failed">
                {getDeleteConfirmationDialog(
                    modalVisible,
                    setModalVisible,
                    handleDeleteClick
                )}
                <DeleteIcon
                    className="delete-icon"
                    onClick={handleModalState(setModalVisible, true)}
                />
                <span>Artwork failed to generate</span>
                <Button
                    className="text-primary-button button-restart"
                    onClick={handleRetryClick}
                >
                    RETRY
                </Button>
            </div>
        );
    }

    if (status === ArtworkStatus.pending) {
        return (
            <div className="thumbnails-item thumbnails-item-process">
                <span>Generating Artwork</span>
                <CircularProgress />
            </div>
        );
    }

    if (status === ArtworkStatus.waiting) {
        return (
            <div className="thumbnails-item thumbnails-item-wait">
                <CircularProgress />
            </div>
        );
    }

    const replaceArtwork = handleReplace(qcId, id, setArtworkReplace);

    return (
        <div
            role="button"
            key={url}
            className={classNames('thumbnails-item', {
                active: id === currentId,
                zooming: isModalOpen === name && id === currentZoomId,
            })}
            onClick={onActiveImgChange(
                currentZoomId,
                setCurrentZoomId,
                thumbnail,
                thumbnailRefs,
                handleZoom,
                id
            )}
        >
            <div
                className={classNames('thumbnails-item__artwork-type', 'circle', {
                    active: id === currentId,
                })}
                onClick={onActiveImgChange(
                    currentId,
                    setCurrentId,
                    thumbnail,
                    thumbnailRefs,
                    onDebouncedImageSelect,
                    id
                )}
            />
            <div className="thumbnails-item__img-wrapper">
                <ContextMenu className="context-menu-replace">
                    <FileImport
                        btnText="Replace"
                        btnClass="content-item-reports__upload"
                        modalTitle="Replace Artwork"
                        action={replaceArtwork}
                        className="artwork"
                        acceptedFiles={['image/jpeg', 'image/jpg']}
                        previewComponent={FileImport.ImagePreview}
                        selectOpts={getSelectOpts(artworkType)}
                        submitBtnText="Replace"
                    />
                    <button className="qc-tools-context-delete-btn" disabled>
                        Delete
                    </button>
                </ContextMenu>
                <img
                    className={classNames('thumbnails-item__img', {
                        active: id === currentId,
                    })}
                    ref={(imageElement) => (thumbnailRefs.current[id] = imageElement)}
                    alt={url}
                    src={url}
                />
            </div>
            <div className="thumbnails-item__artwork-info">
                <span>
                    {resX} x {resY}
                </span>
                <span>{aspectRatio}</span>
                <span>{formatBytes(fileSizeBytes, 0)}</span>
                <span>{convertImageFormat(imageFormat)}</span>
            </div>
        </div>
    );
};

export default memo(QCToolsArtworkThumbnailsItem);
